import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import "./BookingDetailModal.css";

/* ── Inline SVG icons (Lucide-style 1.75 stroke, currentColor) ── */
const Icon = ({ size = 16, fill = "none", stroke = "currentColor", strokeWidth = 1.75, children, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={stroke}
    strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" focusable="false" {...rest}>
    {children}
  </svg>
);
const IconX = (p) => <Icon {...p}><path d="M18 6 6 18M6 6l12 12" /></Icon>;
const IconUser = (p) => <Icon {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></Icon>;
const IconBriefcase = (p) => <Icon {...p}><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></Icon>;
const IconCalendar = (p) => <Icon {...p}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></Icon>;
const IconClock = (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>;
const IconMapPin = (p) => <Icon {...p}><path d="M12 22s-7-6.5-7-12a7 7 0 1 1 14 0c0 5.5-7 12-7 12Z" /><circle cx="12" cy="10" r="2.5" /></Icon>;
const IconMail = (p) => <Icon {...p}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 7 9-7" /></Icon>;
const IconPhone = (p) => <Icon {...p}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.95.36 1.88.7 2.77a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.31-1.31a2 2 0 0 1 2.11-.45c.89.34 1.82.57 2.77.7A2 2 0 0 1 22 16.92Z" /></Icon>;
const IconDollar = (p) => <Icon {...p}><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></Icon>;
const IconCreditCard = (p) => <Icon {...p}><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></Icon>;
const IconSend = (p) => <Icon {...p}><path d="m22 2-7 20-4-9-9-4 20-7Z" /></Icon>;
const IconCheckCircle = (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M8 12.5 11 15.5 16 9.5" /></Icon>;
const IconAlert = (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16v.01" /></Icon>;
const IconLayers = (p) => <Icon {...p}><path d="M12 2 2 7l10 5 10-5-10-5ZM2 17l10 5 10-5M2 12l10 5 10-5" /></Icon>;
const IconHeart = (p) => <Icon {...p}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78Z" /></Icon>;
const IconHash = (p) => <Icon {...p}><path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" /></Icon>;
const IconNote = (p) => <Icon {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6" /></Icon>;
const IconShield = (p) => <Icon {...p}><path d="M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3Z" /></Icon>;
const IconCopy = (p) => <Icon {...p}><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></Icon>;

/* ── Format helpers ── */
const fmtMoney = (n, currency = "CAD") => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(v);
};
const fmtDate = (iso, opts) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", opts || { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
};
const fmtTime = (hhmm) => {
  if (!hhmm) return "—";
  const [hStr, mStr] = String(hhmm).split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr || "0", 10);
  if (Number.isNaN(h)) return hhmm;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
};
const addHoursToTime = (start, hours) => {
  if (!start || hours == null) return null;
  const [h, m] = String(start).split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(h)) return null;
  const total = h + Number(hours);
  return `${String(total).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}`;
};
const ageFromBirthYear = (yr) => {
  const n = parseInt(yr, 10);
  if (!Number.isFinite(n) || n < 1900) return null;
  return new Date().getFullYear() - n;
};

const statusMeta = {
  paid: { label: "Paid", cls: "bdm-pill--accent", Icon: IconCheckCircle },
  confirmed: { label: "Confirmed", cls: "bdm-pill--success", Icon: IconCheckCircle },
  declined: { label: "Declined", cls: "bdm-pill--neutral", Icon: IconAlert },
  pending: { label: "Pending", cls: "bdm-pill--warn", Icon: IconClock },
};

/* ── Field row ── */
const Field = ({ icon: I, label, children, copy }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    if (!copy) return;
    navigator.clipboard?.writeText(String(copy)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="bdm-field">
      {I && <span className="bdm-field__icon"><I size={14} /></span>}
      <div className="bdm-field__body">
        <div className="bdm-field__label">{label}</div>
        <div className="bdm-field__value">
          {children || <span className="bdm-field__empty">—</span>}
          {copy && (
            <button
              type="button"
              className="bdm-field__copy"
              onClick={handleCopy}
              title={copied ? "Copied" : "Copy"}
              aria-label={copied ? "Copied to clipboard" : "Copy to clipboard"}
            >
              <IconCopy size={12} /> {copied ? "Copied" : ""}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Section = ({ icon: I, title, accent, children, right }) => (
  <section className={`bdm-section ${accent ? `bdm-section--${accent}` : ""}`}>
    <header className="bdm-section__head">
      <div className="bdm-section__title">
        {I && <span className="bdm-section__icon"><I size={15} /></span>}
        <span>{title}</span>
      </div>
      {right}
    </header>
    <div className="bdm-section__body">{children}</div>
  </section>
);

/* ── Main modal ── */
export const BookingDetailModal = ({ show, onClose, bookingId, onFetchDetail }) => {
  const [state, setState] = useState({ loading: false, error: null, data: null });
  const dialogRef = useRef(null);

  // Fetch detail on open
  useEffect(() => {
    if (!show || !bookingId || !onFetchDetail) return;
    let cancelled = false;
    setState({ loading: true, error: null, data: null });
    onFetchDetail(bookingId)
      .then((res) => {
        if (cancelled) return;
        setState({ loading: false, error: null, data: res?.data || null });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("BookingDetailModal: fetch failed", err);
        setState({ loading: false, error: "Could not load booking detail. Try again.", data: null });
      });
    return () => { cancelled = true; };
  }, [show, bookingId, onFetchDetail]);

  // Esc + body scroll lock
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    // Move focus into the dialog for screen readers / keyboard nav
    setTimeout(() => dialogRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [show, onClose]);

  if (!show) return null;

  const d = state.data;
  const status = d?.status || "pending";
  const sMeta = statusMeta[status] || statusMeta.pending;
  const totalScheduleHours = (d?.schedule || []).reduce(
    (s, r) => s + Number(r.hours || 0), 0
  );

  const sameDay =
    d?.booking?.start_date &&
    d?.booking?.end_date &&
    new Date(d.booking.start_date).toDateString() === new Date(d.booking.end_date).toDateString();

  return createPortal(
    <div
      className="bdm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bdm-title"
      onClick={onClose}
    >
      <div
        className="bdm-dialog"
        ref={dialogRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="bdm-header">
          <div className="bdm-header__left">
            <div className="bdm-header__crumb" title={`Booking #${bookingId}`}>
              <IconHash size={12} /> Booking #{bookingId}
            </div>
            <h2 id="bdm-title" className="bdm-header__title">
              {d?.service?.care_service_name || (state.loading ? "Loading…" : "Booking Details")}
            </h2>
            <div className="bdm-header__sub">
              <span className={`bdm-pill ${sMeta.cls}`}>
                <sMeta.Icon size={12} /> {sMeta.label}
              </span>
              {d?.booking && (
                <span className="bdm-header__meta">
                  <IconCalendar size={12} />
                  {sameDay
                    ? fmtDate(d.booking.start_date)
                    : `${fmtDate(d.booking.start_date)} → ${fmtDate(d.booking.end_date)}`}
                  <span className="bdm-header__dot">·</span>
                  <IconClock size={12} /> {d.booking.total_hours} hr{d.booking.total_hours !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="bdm-close"
            onClick={onClose}
            aria-label="Close booking detail"
          >
            <IconX size={18} />
          </button>
        </header>

        {/* Body */}
        <div className="bdm-body">
          {state.loading && (
            <div className="bdm-loading">
              <span className="bdm-spinner" />
              <span>Loading booking detail…</span>
            </div>
          )}

          {state.error && !state.loading && (
            <div className="bdm-error" role="alert">
              <IconAlert size={16} /> {state.error}
            </div>
          )}

          {!state.loading && !state.error && d && (
            <>
              {/* Total Price — emphasized at top */}
              <Section icon={IconDollar} title="Total" accent="money">
                <div className="bdm-total">
                  <div className="bdm-total__main">
                    <div className="bdm-total__amount">
                      {fmtMoney(d.pricing?.total_estimated_dollars, d.pricing?.currency)}
                    </div>
                    <div className="bdm-total__label">
                      {d.pricing?.paid ? "Charged" : "Estimated total"}
                    </div>
                  </div>
                  <ul className="bdm-total__breakdown">
                    <li>
                      <span>Hourly rate</span>
                      <span>{fmtMoney(d.pricing?.hourly_rate, d.pricing?.currency)}/hr</span>
                    </li>
                    <li>
                      <span>Hours × rate</span>
                      <span>
                        {d.pricing?.total_hours} × {fmtMoney(d.pricing?.hourly_rate, d.pricing?.currency)} =
                        {" "}
                        {fmtMoney(d.pricing?.subtotal_dollars, d.pricing?.currency)}
                      </span>
                    </li>
                    {d.pricing?.estimated_fee_dollars > 0 && (
                      <li>
                        <span>Processing fee (2.9% + $0.30)</span>
                        <span>+ {fmtMoney(d.pricing.estimated_fee_dollars, d.pricing?.currency)}</span>
                      </li>
                    )}
                    <li className="bdm-total__pay">
                      <span>Payment method</span>
                      <span className="bdm-total__method">
                        {d.pricing?.payment_method === "etransfer"
                          ? <><IconSend size={12} /> e-Transfer</>
                          : <><IconCreditCard size={12} /> Card</>}
                      </span>
                    </li>
                  </ul>
                </div>
              </Section>

              {/* Patient */}
              <Section icon={IconUser} title="Member">
                <div className="bdm-grid">
                  <Field icon={IconUser} label="Name">
                    {d.patient?.first_name} {d.patient?.last_name}
                  </Field>
                  <Field icon={IconCalendar} label="Age">
                    {(() => {
                      const a = ageFromBirthYear(d.patient?.birth_year);
                      return a != null ? `${a} (born ${d.patient.birth_year})` : null;
                    })()}
                  </Field>
                  {d.patient?.phone_number && (
                    <Field icon={IconPhone} label="Phone" copy={d.patient.phone_number}>
                      <a href={`tel:${d.patient.phone_number}`}>{d.patient.phone_number}</a>
                    </Field>
                  )}
                  {d.patient?.email && (
                    <Field icon={IconMail} label="Email" copy={d.patient.email}>
                      <a href={`mailto:${d.patient.email}`}>{d.patient.email}</a>
                    </Field>
                  )}
                  {d.address && (
                    <Field icon={IconMapPin} label="Address">
                      {[d.address.address, d.address.city, d.address.province, d.address.country]
                        .filter(Boolean)
                        .join(", ")}
                    </Field>
                  )}
                </div>

                {(d.patient?.medical_conditions ||
                  d.patient?.allergies ||
                  d.patient?.medications ||
                  d.patient?.special_care) && (
                  <div className="bdm-medical">
                    <div className="bdm-medical__title">
                      <IconHeart size={13} /> Medical
                    </div>
                    <div className="bdm-grid bdm-grid--tight">
                      {d.patient.medical_conditions && (
                        <Field icon={IconShield} label="Conditions">{d.patient.medical_conditions}</Field>
                      )}
                      {d.patient.allergies && (
                        <Field icon={IconAlert} label="Allergies">{d.patient.allergies}</Field>
                      )}
                      {d.patient.medications && (
                        <Field icon={IconHeart} label="Medications">{d.patient.medications}</Field>
                      )}
                      {d.patient.special_care && (
                        <Field icon={IconNote} label="Special care">{d.patient.special_care}</Field>
                      )}
                    </div>
                  </div>
                )}
              </Section>

              {/* Provider */}
              <Section icon={IconBriefcase} title="Provider">
                {d.provider ? (
                  <div className="bdm-provider">
                    <div className="bdm-provider__avatar">
                      {d.provider.img_url
                        ? <img src={d.provider.img_url} alt="" />
                        : <IconUser size={22} />}
                    </div>
                    <div className="bdm-provider__body">
                      <div className="bdm-provider__name">{d.provider.provider_name}</div>
                      {d.provider.title && (
                        <div className="bdm-provider__title">{d.provider.title}</div>
                      )}
                      <div className="bdm-grid bdm-grid--tight bdm-grid--mt">
                        {d.provider.email && (
                          <Field icon={IconMail} label="Email" copy={d.provider.email}>
                            <a href={`mailto:${d.provider.email}`}>{d.provider.email}</a>
                          </Field>
                        )}
                        {d.provider.phone_number && (
                          <Field icon={IconPhone} label="Phone" copy={d.provider.phone_number}>
                            <a href={`tel:${d.provider.phone_number}`}>{d.provider.phone_number}</a>
                          </Field>
                        )}
                        {(d.provider.opening_hour != null || d.provider.closing_hour != null) && (
                          <Field icon={IconClock} label="Working hours">
                            {String(d.provider.opening_hour).padStart(2, "0")}:00 – {String(d.provider.closing_hour).padStart(2, "0")}:00
                          </Field>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bdm-empty">
                    <IconAlert size={14} /> No provider assigned yet.
                  </div>
                )}
              </Section>

              {/* Service */}
              <Section icon={IconLayers} title="Service">
                <div className="bdm-grid">
                  <Field icon={IconBriefcase} label="Service">{d.service?.care_service_name}</Field>
                  <Field icon={IconDollar} label="Hourly rate">
                    {fmtMoney(d.service?.effective_rate, d.pricing?.currency)}/hr
                    {d.service?.provider_price != null && d.service?.hourly_rate != null
                      && Number(d.service.provider_price) !== Number(d.service.hourly_rate) && (
                        <span className="bdm-note">
                          (provider override; service default {fmtMoney(d.service.hourly_rate, d.pricing?.currency)})
                        </span>
                      )}
                  </Field>
                  {d.service?.min_hours != null && (
                    <Field icon={IconClock} label="Minimum hours">
                      {d.service.min_hours} hr{d.service.min_hours !== 1 ? "s" : ""}
                    </Field>
                  )}
                </div>

                {d.sub_services?.length > 0 && (
                  <div className="bdm-subs">
                    <div className="bdm-subs__label">Sub-services</div>
                    <div className="bdm-chip-row">
                      {d.sub_services.map((s) => (
                        <span key={s.sub_service_id} className="bdm-chip" title={s.sub_service_des || ""}>
                          {s.sub_service_name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </Section>

              {/* Schedule */}
              <Section
                icon={IconCalendar}
                title="Schedule"
                right={
                  <span className="bdm-section__meta">
                    <IconClock size={12} /> {totalScheduleHours} hr{totalScheduleHours !== 1 ? "s" : ""} total
                    <span className="bdm-header__dot">·</span>
                    {(d.schedule || []).length} session{(d.schedule || []).length !== 1 ? "s" : ""}
                  </span>
                }
              >
                {(d.schedule || []).length === 0 ? (
                  <div className="bdm-empty"><IconAlert size={14} /> No scheduled time slots.</div>
                ) : (
                  <ul className="bdm-schedule">
                    {d.schedule.map((r, i) => {
                      const end = addHoursToTime(r.start_time, r.hours);
                      const isAccepted = r.acceptance === true;
                      const isCancelled = r.isCancelled === true;
                      return (
                        <li key={i} className={`bdm-schedule__row ${isCancelled ? "bdm-schedule__row--cancelled" : ""}`}>
                          <span className="bdm-schedule__date">
                            {fmtDate(r.date_day, { weekday: "short", month: "short", day: "numeric" })}
                          </span>
                          <span className="bdm-schedule__time">
                            <IconClock size={11} /> {fmtTime(r.start_time)} <span className="bdm-schedule__sep">→</span> {fmtTime(end)}
                          </span>
                          <span className="bdm-schedule__hours">{r.hours} hr{r.hours !== 1 ? "s" : ""}</span>
                          <span className={`bdm-schedule__chip ${
                            isCancelled ? "bdm-schedule__chip--cancel"
                            : isAccepted ? "bdm-schedule__chip--ok"
                            : "bdm-schedule__chip--idle"
                          }`}>
                            {isCancelled ? "Cancelled" : isAccepted ? "Accepted" : "Pending"}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Section>

              {/* e-Transfer (only if applicable) */}
              {d.etransfer && (
                <Section icon={IconSend} title="e-Transfer">
                  <div className="bdm-grid">
                    <Field icon={IconHash} label="Reference code" copy={d.etransfer.reference_code}>
                      <code className="bdm-code">{d.etransfer.reference_code}</code>
                    </Field>
                    <Field icon={IconDollar} label="Expected amount">
                      {fmtMoney((d.etransfer.expected_amount || 0) / 100, d.pricing?.currency)}
                    </Field>
                    <Field icon={IconClock} label="Status">
                      <span className={`bdm-pill ${
                        d.etransfer.status === "matched" || d.etransfer.status === "manual"
                          ? "bdm-pill--accent"
                          : d.etransfer.status === "expired"
                            ? "bdm-pill--neutral"
                            : "bdm-pill--warn"
                      }`}>
                        {d.etransfer.status}
                      </span>
                    </Field>
                    {d.etransfer.created_at && (
                      <Field icon={IconCalendar} label="Created">{fmtDateTime(d.etransfer.created_at)}</Field>
                    )}
                    {d.etransfer.matched_at && (
                      <Field icon={IconCheckCircle} label="Matched">{fmtDateTime(d.etransfer.matched_at)}</Field>
                    )}
                    {d.etransfer.expires_at && !d.etransfer.matched_at && (
                      <Field icon={IconAlert} label="Expires">{fmtDateTime(d.etransfer.expires_at)}</Field>
                    )}
                  </div>
                </Section>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <footer className="bdm-footer">
          <button type="button" className="bdm-btn bdm-btn--ghost" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default BookingDetailModal;
